import React, {useState, useEffect} from 'react';
import styled from 'styled-components';
import {Button} from '@betareader/design-system';
import {Trans, useTranslation} from 'react-i18next';
import {Link, useHistory} from 'react-router-dom';
import {useDispatch, useSelector} from 'react-redux';

import {fetchSwappableBook, resetSwappableBook, fetchSwappables} from '../../modules/book';
import {createReader} from '../../modules/reader';
import {authUtil, imageUtil} from '../../utils';

const DEFAULT_COVER_BACKGROUND =
  'https://storage.googleapis.com/beta-reader-prod.appspot.com/defaults/DEFAULT_COVER_BACKGROUND.png';

const BookList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
`;

const BookRow = styled.li`
  align-items: center;
  border-bottom: 1px solid var(--br-border);
  display: flex;
  flex-wrap: wrap;
  gap: var(--br-space-3);
  padding: var(--br-space-3) 0;
`;

const BookCover = styled.img`
  border-radius: var(--br-control-radius);
  height: 50px;
  object-fit: cover;
  width: 50px;
`;

const Field = styled.label`
  display: grid;
  gap: var(--br-space-2);
`;

const Select = styled.select`
  background: var(--br-surface);
  border: 1px solid var(--br-border);
  border-radius: var(--br-control-radius);
  color: var(--br-text);
  min-height: 40px;
  padding: 0 var(--br-space-2);
`;

export const InviteReader = ({reader, source, onClose}) => {
  const {t} = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const selectedBook = useSelector(state => state.book.swappableBook);
  const swappables = useSelector(state => state.book.swappables);
  const [message, setMessage] = useState('');
  const [existingEntries, setExistingEntries] = useState([]);
  const [selectedVersion, setSelectedVersion] = useState();
  const [sendingInvitation, setSendingInvitation] = useState(false);

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        const idToken = await authUtil.getFreshIdToken();
        if (!active) return;
        dispatch(fetchSwappables(idToken));
        const response = await fetch(
          `${process.env.REACT_APP_API_HOST}${process.env.REACT_APP_API_PATH}/reader-index/user/${reader._id}`,
          {method: 'GET', headers: {'Content-Type': 'application/json', 'br-token': idToken}}
        );
        if (!response.ok) throw new Error('Failed to load reader index');
        const user = await response.json();
        if (active) setExistingEntries(user.existingEntries || []);
      } catch (error) {
        console.error(error);
      }
    };
    load();
    return () => {
      active = false;
      dispatch(resetSwappableBook());
    };
  }, [dispatch, reader._id]);

  const selectBook = async bookId => {
    const idToken = await authUtil.getFreshIdToken();
    setSelectedVersion(undefined);
    dispatch(fetchSwappableBook(idToken, bookId));
  };

  const unselectBook = () => {
    setSelectedVersion(undefined);
    dispatch(resetSwappableBook());
  };

  const inviteUserToBook = async event => {
    event.preventDefault();
    if (!selectedBook || sendingInvitation || !selectedBook.content?.length) return;
    setSendingInvitation(true);
    try {
      const idToken = await authUtil.getFreshIdToken();
      dispatch(createReader(idToken, {
        bookId: selectedBook._id,
        contentVersion: selectedVersion || selectedBook.content[selectedBook.content.length - 1]._id,
        userId: reader._id,
        source,
        sendMail: true,
        message,
      }, success => {
        setSendingInvitation(false);
        if (success && onClose) onClose();
      }));
    } catch (error) {
      setSendingInvitation(false);
      console.error(error);
    }
  };

  const coverUrl = book => imageUtil.getImageUrl({
    originalImageUrl: book?.cover?.highRes?.url ?? DEFAULT_COVER_BACKGROUND,
    operation: 'cropfit',
    size: '100x100',
  });

  return (
    <div>
      <BookList>
        {selectedBook && (
          <BookRow>
            <BookCover src={coverUrl(selectedBook)} alt='' />
            <Button variant='secondary' onClick={() => history.push(`/books/${selectedBook._id}`)}>
              {selectedBook.title}
            </Button>
            <Field>
              <span>{t('Version')}</span>
              <Select value={selectedVersion || selectedBook.content?.[selectedBook.content.length - 1]?._id || ''}
                onChange={event => setSelectedVersion(event.target.value)}>
                {(selectedBook.content || []).map(version => (
                  <option key={version._id} value={version._id}>
                    {t('Version')} {version.versionNumber} · {t('WordCountString', {
                      count: (version.parts || []).reduce((total, part) => total + (part.wordCount || 0), 0),
                    })}
                  </option>
                ))}
              </Select>
            </Field>
            <Button variant='secondary' onClick={unselectBook}>{t('ChangeBook')}</Button>
          </BookRow>
        )}
        {!selectedBook && Array.isArray(swappables) && swappables
          .filter(book => book.privacy !== 'unpublished')
          .map(book => {
            const alreadyInvited = existingEntries.some(entry => entry.book === book._id);
            const canInvite = !alreadyInvited && book.canAddMoreReaders;
            return (
              <BookRow key={book._id}>
                <BookCover src={coverUrl(book)} alt='' />
                <div>
                  <Button variant='secondary' disabled={!canInvite} onClick={() => selectBook(book._id)}>
                    {book.title}
                  </Button>
                  {alreadyInvited && <p>{t('UserAlreadyInvited', {user: reader.displayName})}</p>}
                  {!book.canAddMoreReaders && (
                    <Trans i18nKey='maxReadersLimitMessage'>
                      You have reached your reader limit. <Link to='/products'>Upgrade</Link> to be able to send and approve further invitations.
                    </Trans>
                  )}
                </div>
              </BookRow>
            );
          })}
        {!selectedBook && (!swappables || swappables.length === 0) && (
          <BookRow>
            <Button variant='secondary' onClick={() => history.push('/mymanuscripts')}>
              {t('NothingHere')}
            </Button>
            <Trans i18nKey='NoBookMsg'>
              <p>There is nothing here!</p>
              <p>Do you want to <Link to='/mymanuscripts'>add your own manuscript?</Link></p>
            </Trans>
          </BookRow>
        )}
      </BookList>
      {selectedBook && (
        <form onSubmit={inviteUserToBook}>
          <Field>
            <span>{t('Message')}</span>
            <textarea value={message} placeholder={t('InvitationMessagePlaceholder')}
              onChange={event => setMessage(event.target.value)} rows={4} />
          </Field>
          <Button type='submit' disabled={sendingInvitation || !selectedBook.content?.length}
            aria-busy={sendingInvitation}>
            {sendingInvitation ? t('Sending') : t('Send')}
          </Button>
        </form>
      )}
    </div>
  );
};

export default InviteReader;
